import React, { useState, useMemo } from 'react';

import { FORMS } from '../../schemas/stepMap';
import { projectionCalculator } from '../SummaryPage/Comparison/projectionCalculator';
import graphDataFormatter from '../SummaryPage/Comparison/graphDataFormatter';
import SummaryProjection from '../SummaryPage/SummaryProjection';

const graphSelectOptions = [
  {
    name: 'netMonthlyIncome',
    altName: 'monthlyNOI',
    description: 'New Monthly Income After Operating Expenses',
    label: 'Net Income',
  },
  {
    name: 'equityInProperty',
    altName: 'totalEquity',
    description:
      'Total Equity Based on the Estimated Market Value of the Property',
    label: 'Total Equity',
  },
  {
    name: 'newMonthlyPreTaxCashFlow',
    altName: 'monthlyPreTaxCashFlowBuyAndHold',
    description: 'Annual Pre-Tax Cash Flow',
    label: 'Cash Flow',
  },
  {
    name: 'newTotalRoiYear1',
    altName: 'totalROIYear1BuyAndHoldAmount',
    description: 'Return On Investment',
    label: 'ROI',
  },
  {
    name: 'newMarketValue',
    altName: 'propertyValueProjection',
    description: '',
    label: 'Property Value',
  },
  {
    type: 'stack',
    stackIncludes: [
      {
        name: 'yearOnePrincipalReduction',
        altName: 'newYearOnePrincipalReduction',
        label: 'Principal',
      },
      {
        name: 'interest',
        label: 'Interest',
        derivedFrom: [
          'monthlyDebtService',
          'balloonPayment',
          'yearOnePrincipalReduction',
          'newYearOnePrincipalReduction',
          'newMonthlyPayment',
        ],
        calcFunction: (
          monthlyDebtService,
          balloonPayment,
          yearOnePrincipalReduction,
          newYearOnePrincipalReduction,
          newMonthlyPayment
        ) => {
          if (isValue(monthlyDebtService, yearOnePrincipalReduction)) {
            return (
              monthlyDebtService +
              (balloonPayment || 0) -
              yearOnePrincipalReduction
            );
          }

          if (isValue(newMonthlyPayment, newYearOnePrincipalReduction)) {
            return (
              newMonthlyPayment +
              (balloonPayment || 0) -
              newYearOnePrincipalReduction
            );
          }

          return null;
        },
      },
    ],
    description: 'Mortgage Payments over the years',
    label: 'Amortization',
  },
];

export default function BRRRRProjectionsSummary({
  propertyData,
  scenarioData,
}) {
  const [graph, setGraph] = useState(0);
  const [years, setYears] = useState(30);

  const rehabYears = scenarioData?.rehabLength
    ? Math.floor(scenarioData?.rehabLength / 12)
    : 0;

  const projectionForm = FORMS.projection;

  const brrrrSchema = projectionForm.schema.calculations.schema[1];
  const brrrrFields = brrrrSchema.schema;

  const buyAndHoldSchema = projectionForm.schema.calculations.schema[2];
  const buyAndHoldFields = buyAndHoldSchema.schema;

  const BRRRRData = useMemo(
    () =>
      projectionCalculator(
        years,
        brrrrFields,
        {
          ...propertyData,
          ...scenarioData,
        },
        rehabYears
      ),
    [years, propertyData, scenarioData]
  );

  const buyAndHoldData = rehabYears
    ? projectionCalculator(rehabYears, buyAndHoldFields, {
        ...propertyData,
        ...scenarioData,
      })
    : null;

  const projectionData = buyAndHoldData
    ? buyAndHoldData.concat(BRRRRData)
    : BRRRRData;

  const graphData = graphDataFormatter(
    years,
    projectionData,
    {},
    {},
    graphSelectOptions[graph]?.name,
    graphSelectOptions[graph]?.altName || null,
    null,
    'single',
    graphSelectOptions[graph]
  );

  return (
    <div>
      <SummaryProjection
        options={graphSelectOptions}
        currOption={graph}
        setOption={setGraph}
        years={years}
        setYears={setYears}
        graphData={graphData}
        tableData={projectionData}
        schema={brrrrSchema}
        rehabYears={rehabYears}
        tableLabel="Detailed Projections"
        propertyData={propertyData}
      />
    </div>
  );
}
